import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";

export function ForecastRevenueTile({ data, ewmaAlpha, onAlphaChange }) {
  const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const trend = data.trend_pct ?? 0;
  const up = trend > 0;
  const flat = trend === 0;

  // Combine actual series + 12-month seasonal projection on a single chart.
  // Actual rows get { actual_usd, forecast_usd: null } and projected rows get
  // the inverse — keeps Recharts happy with two distinct lines/bars.
  const combined = [
    ...(data.series || []).map((r) => ({ month: r.month, actual_usd: r.revenue_usd })),
    ...(data.seasonal_next_12_usd || []).map((r) => ({ month: r.month, forecast_usd: r.projected_usd })),
  ];

  return (
    <div className="brut-card p-5" data-testid="forecast-revenue-widget">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> FORECASTED REVENUE · USD
          </div>
          <div className="font-heading text-xl font-bold">EWMA forecast · 12-month seasonal projection</div>
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
            All currencies normalised to USD using stored exchange rates
          </div>
        </div>
        <div
          className={`border border-zinc-900 px-3 py-1.5 font-mono text-xs font-bold flex items-center gap-1.5 ${
            flat ? "bg-zinc-100" : up ? "bg-green-200" : "bg-red-200"
          }`}
          data-testid="forecast-trend-pill"
        >
          {flat ? null : up ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
          {Math.abs(trend).toFixed(1)}% vs prior 3 mo
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="border border-zinc-900 p-4 bg-yellow-50" data-testid="forecast-next-month">
          <div className="label-mono">NEXT MONTH · EWMA</div>
          <div className="font-heading text-3xl font-black mt-1 flex items-center gap-1">
            <DollarSign className="w-6 h-6" />
            {Number(data.ewma_next_month_usd || data.forecasted_next_month_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[11px] font-mono text-zinc-600 mt-1">α = {(data.ewma_alpha ?? ewmaAlpha)?.toFixed(2)} · recent months weighted higher</div>
        </div>
        <div className="border border-zinc-900 p-4 bg-blue-50" data-testid="forecast-next-12-total">
          <div className="label-mono">NEXT 12 MO · TOTAL</div>
          <div className="font-heading text-3xl font-black mt-1 flex items-center gap-1">
            <DollarSign className="w-6 h-6" />
            {Number(data.seasonal_total_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[11px] font-mono text-zinc-600 mt-1">
            {data.has_seasonal_history
              ? "Seasonally adjusted from 12-mo history"
              : "Flat EWMA × 12 (need 12 mo of data for seasonality)"}
          </div>
        </div>
        <div className="border border-zinc-900 p-4" data-testid="forecast-current-avg">
          <div className="label-mono">CURRENT 3-MO AVG</div>
          <div className="font-heading text-2xl font-black mt-1 text-zinc-900">{fmtUsd(data.current_3m_avg_usd)}</div>
          <div className="text-[11px] font-mono text-zinc-600 mt-1">Last 3 months invoiced</div>
        </div>
        <div className="border border-zinc-900 p-4" data-testid="forecast-prior-avg">
          <div className="label-mono">PRIOR 3-MO AVG</div>
          <div className="font-heading text-2xl font-black mt-1 text-zinc-500">{fmtUsd(data.prior_3m_avg_usd)}</div>
          <div className="text-[11px] font-mono text-zinc-600 mt-1">Months 4–6 ago</div>
        </div>
      </div>

      {/* EWMA alpha slider — lets the user tune recency bias */}
      <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="forecast-alpha-control">
        <div className="label-mono">EWMA RECENCY BIAS (α)</div>
        <input
          type="range"
          min="0.1"
          max="0.95"
          step="0.05"
          value={ewmaAlpha}
          onChange={(e) => onAlphaChange?.(parseFloat(e.target.value))}
          className="flex-1 accent-zinc-900 max-w-md"
          data-testid="forecast-alpha-slider"
        />
        <span className="font-mono text-xs font-bold w-12 text-right">{ewmaAlpha.toFixed(2)}</span>
        <span className="font-mono text-[10px] text-zinc-500">
          0.10 = slow / smooth · 0.95 = fast / spiky
        </span>
      </div>

      <div className="mt-4 border-t border-zinc-200 pt-3">
        <div className="label-mono mb-2">ACTUAL + 12-MONTH PROJECTION (USD)</div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={combined}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="month" fontSize={9} tickFormatter={(m) => m?.slice(2).replace("-", "/")} interval="preserveStartEnd" />
              <YAxis fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ border: "2px solid #18181B", borderRadius: 0, fontSize: 11, fontFamily: "monospace" }}
                formatter={(v) => fmtUsd(v)}
              />
              <Bar dataKey="actual_usd" fill="#18181B" name="Actual" />
              <Bar dataKey="forecast_usd" fill="#FACC15" stroke="#18181B" strokeWidth={1} name="Forecast" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-zinc-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-zinc-900"></span> ACTUAL</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-400 border border-zinc-900"></span> SEASONAL FORECAST</span>
        </div>
      </div>
    </div>
  );
}

// ----------------------------- My Team's Performance -----------------------------
